import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, formatPrice } from "../lib/api";
import ProductSearchBox from "../components/ProductSearchBox";
import useTicketReprint from "../hooks/useTicketReprint";
import { formatDateTimeMx } from "../lib/datetimeMx";
import { toast } from "sonner";
import { Minus, Plus, Trash2, Lock, Unlock, Printer } from "lucide-react";

export default function AdminPosPage() {
    const emptyDeduction = () => ({ concept: "", amount: "" });
    const [session, setSession] = useState(null);
    const [loadingSession, setLoadingSession] = useState(true);
    const [openingFloat, setOpeningFloat] = useState("0");
    const [cart, setCart] = useState([]);
    const [payOpen, setPayOpen] = useState(false);
    const [received, setReceived] = useState("");
    const [closeOpen, setCloseOpen] = useState(false);
    const [countedCash, setCountedCash] = useState("");
    const [deductions, setDeductions] = useState([emptyDeduction()]);
    const [summary, setSummary] = useState(null);
    const [lastSale, setLastSale] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyItems, setHistoryItems] = useState([]);
    const [historyQuery, setHistoryQuery] = useState("");
    const { reprint, ensureSettings } = useTicketReprint();
    const receivedInputRef = useRef(null);

    useEffect(() => {
        if (!payOpen) return;
        const id = setTimeout(() => {
            if (receivedInputRef.current) {
                receivedInputRef.current.focus();
                receivedInputRef.current.select();
            }
        }, 0);
        return () => clearTimeout(id);
    }, [payOpen]);

    const loadSession = useCallback(() => {
        setLoadingSession(true);
        return api.get("/pos/session/current")
            .then((r) => setSession(r.data))
            .catch((err) => {
                if (err.response?.status === 404) {
                    setSession(null);
                    return;
                }
                toast.error(formatApiError(err));
                setSession(null);
            })
            .finally(() => setLoadingSession(false));
    }, []);

    useEffect(() => {
        loadSession();
    }, [loadSession]);

    const total = useMemo(
        () => cart.reduce((s, l) => s + l.unit_price * l.qty, 0),
        [cart],
    );

    const addToCart = (p) => {
        const stock = p.stock ?? 0;
        setCart((prev) => {
            const idx = prev.findIndex((l) => l.product_id === p.id);
            if (idx >= 0) {
                const next = [...prev];
                if (stock > 0 && next[idx].qty >= stock) {
                    toast.error(`Stock máximo: ${stock}`);
                    return prev;
                }
                next[idx] = { ...next[idx], qty: next[idx].qty + 1, stock };
                return next;
            }
            if (stock < 1) {
                toast.warning(`«${p.name}» sin stock en sistema; puedes venderlo igual si lo tienes en mostrador.`);
            }
            return [
                ...prev,
                {
                    product_id: p.id,
                    name: p.name,
                    sku: p.sku,
                    unit_price: p.price,
                    qty: 1,
                    stock,
                },
            ];
        });
    };

    const setQty = (productId, delta) => {
        setCart((prev) =>
            prev
                .map((l) => {
                    if (l.product_id !== productId) return l;
                    const nq = l.qty + delta;
                    if (nq < 1) return null;
                    if (l.stock > 0 && nq > l.stock) {
                        toast.error(`Stock máximo: ${l.stock}`);
                        return l;
                    }
                    return { ...l, qty: nq };
                })
                .filter(Boolean),
        );
    };

    const openSession = async () => {
        try {
            const { data } = await api.post("/pos/session/open", {
                opening_float: parseFloat(openingFloat) || 0,
            });
            setSession(data);
            toast.success("Caja abierta");
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (typeof detail === "string" && detail.includes("turno abierto")) {
                await loadSession();
                toast.info("Ya había un turno abierto; se cargó la caja activa.");
                return;
            }
            toast.error(formatApiError(e));
        }
    };

    const checkout = async () => {
        const amt = parseFloat(received);
        if (Number.isNaN(amt) || amt < total) {
            toast.error("Monto recibido insuficiente");
            return;
        }
        try {
            const { data: sale } = await api.post("/pos/sales", {
                items: cart.map((l) => ({ product_id: l.product_id, qty: l.qty })),
                amount_received: amt,
            });
            setLastSale(sale);
            toast.success(`Venta ${sale.ticket_number || ""} registrada`);
            await ensureSettings();
            try {
                await reprint(sale);
            } catch (err) {
                toast.warning(err.message || "Permite ventanas emergentes para imprimir");
            }
            setCart([]);
            setPayOpen(false);
            setReceived("");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const reprintLast = () => {
        if (!lastSale) {
            toast.error("No hay ticket reciente en esta sesión");
            return;
        }
        reprint(lastSale);
    };

    const loadHistory = async () => {
        setHistoryLoading(true);
        try {
            const params = { limit: 30 };
            if (historyQuery.trim()) params.ticket = historyQuery.trim();
            const { data } = await api.get("/pos/sales", { params });
            setHistoryItems(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setHistoryLoading(false);
        }
    };

    const openHistory = () => {
        setHistoryOpen(true);
        loadHistory();
    };

    const loadSummaryAndClose = async () => {
        if (!session) return;
        try {
            const { data } = await api.get(`/pos/session/${session.id}/summary`);
            setSummary(data);
            setDeductions([emptyDeduction()]);
            setCountedCash("");
            setCloseOpen(true);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const parsedDeductions = deductions
        .map((d) => ({
            concept: (d.concept || "").trim(),
            amount: parseFloat(d.amount),
        }))
        .filter((d) => d.concept && !Number.isNaN(d.amount) && d.amount > 0);
    const deductionsTotal = parsedDeductions.reduce((sum, d) => sum + d.amount, 0);
    const expectedAdjusted = Math.max(0, (summary?.expected_cash || 0) - deductionsTotal);
    const closeDiff = Number.isNaN(parseFloat(countedCash))
        ? null
        : Number((parseFloat(countedCash) - expectedAdjusted).toFixed(2));
    const canCloseSession = closeDiff !== null && Math.abs(closeDiff) < 0.01;

    const closeSession = async () => {
        const counted = parseFloat(countedCash);
        if (Number.isNaN(counted)) {
            toast.error("Ingresa el efectivo contado");
            return;
        }
        try {
            await api.post("/pos/session/close", {
                closing_cash_counted: counted,
                deductions: parsedDeductions,
            });
            toast.success("Turno cerrado");
            setSession(null);
            setCloseOpen(false);
            setSummary(null);
            setCart([]);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    if (loadingSession) {
        return <div className="p-10 font-mono text-sm">Cargando caja…</div>;
    }

    if (!session) {
        return (
            <div className="p-6 md:p-10 max-w-lg" data-testid="pos-open-session">
                <span className="lz-section-label">Punto de venta</span>
                <h1 className="font-display text-4xl font-bold uppercase mb-6">Abrir caja</h1>
                <p className="text-sm text-[var(--lz-navy)]/70 mb-4">
                    Si el dashboard indica «Caja abierta» pero ves esta pantalla, pulsa «Recargar caja».
                </p>
                <div className="border-2 border-[var(--lz-navy)] bg-white p-6 space-y-4">
                    <label className="block text-xs font-bold uppercase tracking-widest">
                        Fondo inicial (efectivo en caja)
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={openingFloat}
                            onChange={(e) => setOpeningFloat(e.target.value)}
                            className="lz-input w-full mt-2 font-mono"
                            data-testid="pos-opening-float"
                        />
                    </label>
                    <button type="button" onClick={openSession} className="lz-btn lz-btn-primary w-full" data-testid="pos-open-btn">
                        <Unlock className="w-4 h-4" /> Abrir turno
                    </button>
                    <button type="button" onClick={loadSession} className="lz-btn lz-btn-outline w-full">
                        Recargar caja
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6" data-testid="pos-page">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b-2 border-[var(--lz-navy)] pb-4 mb-6">
                <div>
                    <span className="lz-section-label">Punto de venta</span>
                    <h1 className="font-display text-3xl font-bold uppercase">Caja abierta</h1>
                    <p className="text-xs font-mono text-[var(--lz-navy)]/60 mt-1">
                        Desde {formatDateTimeMx(session.opened_at)} · Fondo {formatPrice(session.opening_float)}
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {lastSale && (
                        <button type="button" onClick={reprintLast} className="lz-btn lz-btn-outline text-xs" data-testid="pos-reprint">
                            <Printer className="w-4 h-4" /> Reimprimir ({lastSale.ticket_number})
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={openHistory}
                        className="lz-btn lz-btn-outline text-xs"
                        data-testid="pos-history-btn"
                    >
                        <Printer className="w-4 h-4" /> Historial / Reimprimir
                    </button>
                    <button type="button" onClick={loadSession} className="lz-btn lz-btn-outline text-xs">
                        Actualizar
                    </button>
                    <button
                        type="button"
                        onClick={loadSummaryAndClose}
                        disabled={cart.length > 0}
                        className="lz-btn lz-btn-outline"
                        title={cart.length > 0 ? "Vacía el ticket antes de cerrar" : ""}
                        data-testid="pos-close-turn-btn"
                    >
                        <Lock className="w-4 h-4" /> Cerrar turno
                    </button>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="border-2 border-[var(--lz-navy)] bg-white p-4">
                    <div className="text-xs font-bold uppercase tracking-widest text-[var(--lz-orange)] mb-3">Buscar producto</div>
                    <ProductSearchBox
                        variant="pos"
                        showStock
                        limit={25}
                        onSelect={addToCart}
                        placeholder="Nombre o código…"
                        inputTestId="pos-search-input"
                        className="w-full"
                    />
                    <p className="text-[10px] text-[var(--lz-navy)]/50 mt-2">Clic en un resultado para agregar al ticket</p>
                </div>

                <div className="border-2 border-[var(--lz-navy)] bg-white flex flex-col min-h-[400px]">
                    <div className="bg-[var(--lz-navy)] text-white px-4 py-3 text-xs font-bold uppercase tracking-widest">
                        Ticket actual
                    </div>
                    <div className="flex-1 overflow-auto divide-y divide-[#0f1419]/10">
                        {cart.length === 0 && (
                            <p className="p-8 text-center text-sm text-[var(--lz-navy)]/50">Agrega productos desde la búsqueda</p>
                        )}
                        {cart.map((l) => (
                            <div key={l.product_id} className="p-3 flex gap-2 items-start" data-testid={`pos-line-${l.product_id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm uppercase truncate">{l.name}</div>
                                    <div className="text-[10px] font-mono text-[var(--lz-navy)]/60">
                                        SKU {l.sku}
                                        {l.stock > 0 ? ` · Stock ${l.stock}` : " · Sin stock"}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 border border-[var(--lz-navy)]">
                                    <button type="button" onClick={() => setQty(l.product_id, -1)} className="p-1.5 hover:bg-[#f3f4f6]"><Minus className="w-3 h-3" /></button>
                                    <span className="w-8 text-center font-mono text-sm">{l.qty}</span>
                                    <button type="button" onClick={() => setQty(l.product_id, 1)} className="p-1.5 hover:bg-[#f3f4f6]"><Plus className="w-3 h-3" /></button>
                                </div>
                                <div className="font-mono font-bold w-20 text-right">{formatPrice(l.unit_price * l.qty)}</div>
                                <button type="button" onClick={() => setCart((c) => c.filter((x) => x.product_id !== l.product_id))} className="p-1 text-red-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        ))}
                    </div>
                    <div className="border-t-2 border-[var(--lz-navy)] p-4 space-y-3">
                        <div className="flex justify-between font-display text-2xl font-bold">
                            <span>TOTAL</span>
                            <span data-testid="pos-total">{formatPrice(total)}</span>
                        </div>
                        <button
                            type="button"
                            disabled={cart.length === 0}
                            onClick={() => { setReceived(String(Math.ceil(total))); setPayOpen(true); }}
                            className="lz-btn lz-btn-primary w-full disabled:opacity-40"
                            data-testid="pos-checkout-btn"
                        >
                            Cobrar en efectivo
                        </button>
                    </div>
                </div>
            </div>

            {payOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white border-2 border-[var(--lz-navy)] w-full max-w-md p-6 shadow-[8px_4px_0_var(--lz-navy)]">
                        <h2 className="font-display text-2xl font-bold uppercase mb-4">Cobro en efectivo</h2>
                        <p className="font-display text-3xl font-bold mb-4">{formatPrice(total)}</p>
                        <label className="block text-xs font-bold uppercase mb-2">
                            Recibido
                            <input
                                ref={receivedInputRef}
                                type="number"
                                min="0"
                                step="0.01"
                                value={received}
                                onChange={(e) => setReceived(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                className="lz-input w-full mt-1 font-mono text-lg"
                                data-testid="pos-amount-received"
                                autoFocus
                            />
                        </label>
                        <p className="text-sm font-mono mt-2">
                            Cambio:{" "}
                            <strong>
                                {formatPrice(Math.max(0, (parseFloat(received) || 0) - total))}
                            </strong>
                        </p>
                        <div className="flex gap-2 mt-6">
                            <button type="button" onClick={() => setPayOpen(false)} className="lz-btn lz-btn-outline flex-1">Cancelar</button>
                            <button type="button" onClick={checkout} className="lz-btn lz-btn-primary flex-1" data-testid="pos-confirm-sale">Confirmar venta</button>
                        </div>
                    </div>
                </div>
            )}

            {closeOpen && summary && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white border-2 border-[var(--lz-navy)] w-full max-w-lg p-6 max-h-[90vh] overflow-auto">
                        <h2 className="font-display text-2xl font-bold uppercase mb-4">Corte de caja</h2>
                        <dl className="space-y-2 text-sm font-mono mb-4">
                            <div className="flex justify-between"><dt>Fondo inicial</dt><dd>{formatPrice(summary.opening_float)}</dd></div>
                            <div className="flex justify-between"><dt>Ventas ({summary.sales_count})</dt><dd>{formatPrice(summary.sales_total)}</dd></div>
                            <div className="flex justify-between border-t pt-2"><dt>Efectivo esperado (sin ajustes)</dt><dd>{formatPrice(summary.expected_cash)}</dd></div>
                            <div className="flex justify-between"><dt>Ajustes por salida</dt><dd>-{formatPrice(deductionsTotal)}</dd></div>
                            <div className="flex justify-between font-bold"><dt>Efectivo esperado (ajustado)</dt><dd>{formatPrice(expectedAdjusted)}</dd></div>
                        </dl>
                        <div className="border border-[var(--lz-navy)]/15 p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xs font-bold uppercase tracking-widest">Ajustes / salidas de caja</h3>
                                <button
                                    type="button"
                                    className="lz-btn lz-btn-outline text-[10px] px-2 py-1"
                                    onClick={() => setDeductions((prev) => [...prev, emptyDeduction()])}
                                >
                                    <Plus className="w-3 h-3" /> Agregar
                                </button>
                            </div>
                            {deductions.map((d, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                                    <label className="col-span-7 text-[10px] font-bold uppercase">
                                        Concepto
                                        <input
                                            type="text"
                                            className="lz-input w-full mt-1 text-xs"
                                            placeholder="Ej. Pago de flete"
                                            value={d.concept}
                                            onChange={(e) => setDeductions((prev) => prev.map((x, i) => i === idx ? { ...x, concept: e.target.value } : x))}
                                        />
                                    </label>
                                    <label className="col-span-4 text-[10px] font-bold uppercase">
                                        Monto
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="lz-input w-full mt-1 text-xs font-mono"
                                            value={d.amount}
                                            onChange={(e) => setDeductions((prev) => prev.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))}
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        className="col-span-1 text-red-600 p-2"
                                        onClick={() => setDeductions((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : [emptyDeduction()])}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <label className="block text-xs font-bold uppercase mb-2">
                            Efectivo contado en caja
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={countedCash}
                                onChange={(e) => setCountedCash(e.target.value)}
                                className="lz-input w-full mt-1 font-mono"
                                data-testid="pos-counted-cash"
                            />
                        </label>
                        {countedCash && (
                            <p className="text-sm mt-2">
                                Diferencia:{" "}
                                <strong className={(closeDiff ?? 0) < 0 ? "text-red-600" : "text-green-700"}>
                                    {formatPrice(closeDiff ?? 0)}
                                </strong>
                            </p>
                        )}
                        {closeDiff !== null && closeDiff !== 0 && (
                            <p className="text-xs font-bold text-red-600 mt-1">
                                No puedes cerrar el turno hasta que la diferencia sea exactamente 0.
                            </p>
                        )}
                        <div className="flex gap-2 mt-6">
                            <button type="button" onClick={() => setCloseOpen(false)} className="lz-btn lz-btn-outline flex-1">Cancelar</button>
                            <button
                                type="button"
                                onClick={closeSession}
                                disabled={!canCloseSession}
                                className="lz-btn lz-btn-primary flex-1 disabled:opacity-40"
                                data-testid="pos-confirm-close"
                            >
                                Cerrar turno
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {historyOpen && (
                <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="pos-history-modal">
                    <div className="bg-white border-2 border-[var(--lz-navy)] w-full max-w-2xl max-h-[90vh] overflow-auto shadow-[8px_4px_0_var(--lz-navy)]">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--lz-navy)]/20">
                            <h2 className="font-display text-xl font-bold uppercase">Historial de tickets</h2>
                            <button type="button" onClick={() => setHistoryOpen(false)} className="text-xs font-bold uppercase">
                                Cerrar
                            </button>
                        </div>
                        <div className="p-4 space-y-4">
                            <div className="flex flex-wrap gap-2 items-end">
                                <label className="flex-1 min-w-[180px] text-xs font-bold uppercase tracking-widest">
                                    Folio (ej. A00000012)
                                    <input
                                        type="text"
                                        value={historyQuery}
                                        onChange={(e) => setHistoryQuery(e.target.value)}
                                        className="lz-input w-full mt-1 font-mono text-xs"
                                        placeholder="Deja vacío para ver los últimos"
                                    />
                                </label>
                                <button
                                    type="button"
                                    onClick={loadHistory}
                                    className="lz-btn lz-btn-primary text-xs"
                                    disabled={historyLoading}
                                >
                                    {historyLoading ? "Buscando…" : "Buscar"}
                                </button>
                            </div>

                            <div className="border border-[var(--lz-navy)]/20 max-h-[420px] overflow-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-[var(--lz-navy)] text-white text-[10px] uppercase tracking-widest">
                                            <th className="p-2 text-left">Fecha</th>
                                            <th className="p-2 text-left">Folio</th>
                                            <th className="p-2 text-right">Total</th>
                                            <th className="p-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#0f1419]/10">
                                        {historyItems.map((sale) => (
                                            <tr key={sale.id}>
                                                <td className="p-2 font-mono">
                                                    {formatDateTimeMx(sale.created_at)}
                                                </td>
                                                <td className="p-2 font-mono">{sale.ticket_number || "—"}</td>
                                                <td className="p-2 font-mono text-right">{formatPrice(sale.total)}</td>
                                                <td className="p-2 text-right">
                                                    <button
                                                        type="button"
                                                        className="lz-btn lz-btn-outline text-[10px]"
                                                        onClick={() => reprint(sale)}
                                                    >
                                                        <Printer className="w-3 h-3" /> Reimprimir
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {historyItems.length === 0 && !historyLoading && (
                                            <tr>
                                                <td colSpan={4} className="p-4 text-center text-[var(--lz-navy)]/50">
                                                    Sin ventas que coincidan con la búsqueda.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
